import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SAFETY_STATUS_COLORS, RISK_KEYS, SafetyDashboardPayload } from '../../safety-dashboard.model';
import { SafetyDashboardStore } from '../../safety-dashboard.store';
import Chart from 'chart.js/auto';

@Component({
  selector: 'app-safety-risk',
  templateUrl: './safety-risk.component.html',
  styleUrls: ['./safety-risk.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SafetyRiskComponent implements OnInit, OnDestroy {
  loading = false;
  hasData = false;
  riskAvgs: { label: string; value: number; color: string }[] = [];

  private sub = new Subscription();
  private charts: any[] = [];
  private payload: SafetyDashboardPayload | null = null;

  private readonly riskColors = ['#5caeff', '#ff8b4c', '#f5c451', '#ff5d68', '#c0392b'];
  private readonly statusOrder = ['پرریسک', 'نیازمند توجه', 'نسبتاً ایمن', 'ایمن'];

  constructor(private store: SafetyDashboardStore, private cdr: ChangeDetectorRef) {
    Chart.defaults.font.family = "'vazir', Tahoma, sans-serif";
  }

  ngOnInit(): void {
    this.sub.add(this.store.loading$.subscribe((v) => { this.loading = v; this.cdr.markForCheck(); }));
    this.sub.add(this.store.payload$.subscribe((p) => { this.payload = p; this.refresh(); }));
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.destroyCharts();
  }

  toEn(n: number | null | undefined, fraction = 1): string {
    if (n === null || n === undefined || isNaN(Number(n))) return '—';
    return Number(n).toLocaleString('en-US', { maximumFractionDigits: fraction });
  }

  trackByAvg(_: number, r: { label: string }): string {
    return r.label;
  }

  private refresh(): void {
    const p = this.payload;
    this.hasData = !!p && (!!p.riskAverages || (p.topRiskVehicles?.length ?? 0) > 0);
    if (!this.hasData || !p || !p.riskAverages) {
      this.riskAvgs = [];
      if (!this.hasData) {
        this.destroyCharts();
        this.cdr.markForCheck();
        return;
      }
    }
    const r = p!.riskAverages!;
    this.riskAvgs = RISK_KEYS.map((rk, i) => ({
      label: rk.label,
      value: Number((r as any)[rk.key]) || 0,
      color: this.riskColors[i % this.riskColors.length],
    }));
    this.cdr.markForCheck();
    const snapshot = p!;
    setTimeout(() => this.renderCharts(snapshot), 60);
  }

  private destroyCharts(): void {
    this.charts.forEach((c) => { try { c.destroy(); } catch {} });
    this.charts = [];
  }

  private canvas(id: string): HTMLCanvasElement | null {
    return document.getElementById(id) as HTMLCanvasElement | null;
  }

  private renderCharts(p: SafetyDashboardPayload): void {
    this.destroyCharts();
    if (!this.hasData) return;

    // ---- heatmap از Table2 (حداکثر ۱۵ ردیف آماده سرور) ----
    const heatData = (p.topRiskVehicles || []).slice(0, 15);
    const heatEl = this.canvas('riskHeatmap');
    if (heatEl && heatData.length) {
      this.charts.push(
        new Chart(heatEl, {
          type: 'bar',
          data: {
            labels: heatData.map((x) => (x.strVehicleNo ? x.strVehicleNo : (x.strFullName || '').slice(0, 12))),
            datasets: RISK_KEYS.map((rk, idx) => ({
              label: rk.label,
              data: heatData.map((x) => Number((x as any)[rk.vehicleKey]) || 0),
              backgroundColor: this.riskColors[idx % this.riskColors.length],
              borderRadius: 4,
            })) as any,
          },
          options: {
            maintainAspectRatio: false,
            indexAxis: 'y',
            scales: {
              x: { stacked: true, max: 500, ticks: { color: '#8195a6', font: { size: 9 } }, grid: { color: '#1d2b36' } },
              y: { stacked: true, ticks: { color: '#b9c7d2', font: { size: 10 } }, grid: { display: false } },
            },
            plugins: { legend: { position: 'bottom', labels: { color: '#aebdca', font: { size: 10 }, boxWidth: 10 } } },
          } as any,
        })
      );
    }

    const stackEl = this.canvas('riskStack');
    if (stackEl) {
      const byStatus = p.riskByStatus?.length ? p.riskByStatus : fallbackRiskByStatus(p);
      const labels = byStatus.length ? byStatus.map((x) => x.SafetyStatus) : this.statusOrder;
      const values = byStatus.length
        ? byStatus.map((x) => x.AvgTotalRisk)
        : this.statusOrder.map(() => 0);
      this.charts.push(
        new Chart(stackEl, {
          type: 'bar',
          data: {
            labels,
            datasets: [{
              label: 'میانگین ریسک کل',
              data: values,
              backgroundColor: labels.map((s) => SAFETY_STATUS_COLORS[s] || '#647889'),
              borderRadius: 8,
            }],
          },
          options: {
            maintainAspectRatio: false,
            scales: {
              y: { beginAtZero: true, ticks: { color: '#8195a6', font: { size: 10 } }, grid: { color: '#1d2b36' } },
              x: { ticks: { color: '#b9c7d2', font: { size: 11 } }, grid: { display: false } },
            },
            plugins: { legend: { display: false } },
          } as any,
        })
      );
    }

    const smallIds = ['riskSpeed', 'riskDecel', 'riskAccel', 'riskRep', 'riskPattern'];
    RISK_KEYS.forEach((rk, i) => {
      const el = this.canvas(smallIds[i]);
      if (!el || !p.riskAverages) return;
      this.charts.push(
        new Chart(el, {
          type: 'bar',
          data: {
            labels: ['میانگین'],
            datasets: [{ data: [Number((p.riskAverages as any)[rk.key]) || 0], backgroundColor: this.riskColors[i % this.riskColors.length], borderRadius: 8 }],
          },
          options: {
            maintainAspectRatio: false,
            scales: {
              y: { beginAtZero: true, max: 100, ticks: { color: '#8195a6', font: { size: 9 } }, grid: { color: '#1d2b36' } },
              x: { ticks: { display: false }, grid: { display: false } },
            },
            plugins: { legend: { display: false } },
          } as any,
        })
      );
    });
  }
}

function fallbackRiskByStatus(p: SafetyDashboardPayload): { SafetyStatus: string; AvgTotalRisk: number }[] {
  if (!p.vehicles.length) return [];
  const acc = new Map<string, { sum: number; n: number }>();
  for (const v of p.vehicles) {
    const s = v.SafetyStatus || 'نامشخص';
    let e = acc.get(s);
    if (!e) { e = { sum: 0, n: 0 }; acc.set(s, e); }
    e.sum += Number(v.TotalRisk) || 0;
    e.n += 1;
  }
  return [...acc.entries()].map(([SafetyStatus, e]) => ({
    SafetyStatus,
    AvgTotalRisk: e.n ? e.sum / e.n : 0,
  }));
}
